import type { ReactNode } from "react";
import type { BlogSnapshotStore } from "@shared/blog";

import { Link } from "@/components/Link";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { classifyBlogHref } from "@/lib/blog/links";
import { blogLinkEventProps } from "@/lib/blog/tracking";
import { useBlogPost } from "./BlogPostContext";

export const BLOG_LINK_CLASS =
  "text-warmOrange-700 underline underline-offset-4 hover:text-warmOrange-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warmOrange-500 focus-visible:ring-offset-2 rounded motion-reduce:transition-none";

const isAbsoluteHttp = (href: string) => /^https?:\/\//i.test(href);

/**
 * The MDX `a` override and the base of every blog link primitive (StoreCard, CategoryLink,
 * BlogLink). It is the ONE place `blog_link_clicked` is captured for blog content (D-16): call
 * sites never track. Internal root-relative hrefs render the Vike Link; absolute http(s) hrefs open
 * in a new tab with the opener severed. Nothing here reads a browser global during render.
 */
export function BlogAnchor({
  href,
  children,
  className,
  store,
  title,
}: {
  href?: string;
  children?: ReactNode;
  className?: string;
  store?: BlogSnapshotStore;
  title?: string;
}) {
  const { slug, snapshot, surface } = useBlogPost();
  const { trackBlog } = usePostHogTracking();
  const safeHref = href ?? "";
  const classes = className ?? BLOG_LINK_CLASS;

  const handleClick = () => {
    const target = classifyBlogHref(safeHref);
    const resolvedStore =
      store ??
      (target?.targetType === "store" && target.targetSlug && snapshot
        ? snapshot.stores[target.targetSlug]
        : undefined);
    const props = blogLinkEventProps({
      href: safeHref,
      surface,
      postSlug: surface === "blog_post" && slug ? slug : undefined,
      store: resolvedStore,
    });
    if (props) trackBlog.linkClicked(props);
  };

  if (safeHref.startsWith("/") && !safeHref.startsWith("//")) {
    return (
      <Link to={safeHref} className={classes} title={title} onClick={handleClick}>
        {children}
      </Link>
    );
  }

  if (isAbsoluteHttp(safeHref)) {
    return (
      <a
        href={safeHref}
        className={classes}
        title={title}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
      >
        {children}
      </a>
    );
  }

  // Hash anchors (TOC), mailto and anything else: plain same-tab anchor. blogLinkEventProps
  // returns null for these, so no event fires.
  return (
    <a href={safeHref || undefined} className={classes} title={title}>
      {children}
    </a>
  );
}

export default BlogAnchor;
